/**
 * Deterministic, non-authoritative delivery breakdown for Feature Handoff.
 *
 * Exact source / contractual criteria remain the agreement.
 * Titles, bullets, and delivery-shape copy are guidance for founders/engineers.
 * Vague missing details become questions — never invented requirements.
 */

function normalize(text) {
  let out = String(text || "").normalize("NFC");
  for (let i = 0; i < 8; i++) {
    const next = out
      .replace(/\\+([.!?,;:'"”’)\]}])/g, "$1")
      .replace(/\\{2,}/g, "")
      .replace(/\\+$/g, "");
    if (next === out) break;
    out = next;
  }
  return out.trim().replace(/\s+/g, " ");
}

function includesAny(haystack, needles) {
  const h = haystack.toLowerCase();
  return needles.some((n) => h.includes(String(n).toLowerCase()));
}

function hasConcreteDuration(text) {
  return /\b\d+\s*(?:ms|milliseconds?|s|secs?|seconds?|m|mins?|minutes?|h|hours?)\b/i.test(
    text
  );
}

function hasConcreteRouteScope(text) {
  return /\b(all requests|every request|all routes|every route|only (?:these|selected|specific) routes?|\/[a-z0-9_\-/{}.:]+)\b/i.test(
    text
  );
}

function hasConcreteErrorBody(text) {
  return /\b(status code|http \d{3}|json body|error (?:code|message|payload)|response body|error shape)\b/i.test(
    text
  );
}

/**
 * Short display title for a cited promise. Guidance only.
 */
export function derivePromiseDisplayTitle(sourceText) {
  const text = normalize(sourceText);
  if (!text) return "Promise";

  // More specific coverage / quality promises first — timeout wording inside a
  // testing/scalability sentence must not reuse the core timeout title.
  if (includesAny(text, ["automated tests", "tests that prove", "tests that cover"])) {
    return "Cover with automated tests";
  }
  if (
    includesAny(text, ["concurrent", "usage grows", "unbounded"]) &&
    includesAny(text, ["timeout", "forever", "safe"])
  ) {
    return "Stay safe as usage grows";
  }
  if (includesAny(text, ["revise the handoff", "both product and engineering"])) {
    return "Keep the handoff editable for both parties";
  }
  if (includesAny(text, ["clarifications and engineer notes", "same agreement"])) {
    return "Keep clarifications visible to both parties";
  }
  if (includesAny(text, ["renderrepo handoff", "fully agreed"])) {
    return "Show clear agreement status in the app";
  }

  if (includesAny(text, ["timeout error", "send back a timeout", "return a timeout"])) {
    if (includesAny(text, ["stop", "cancel", "abort", "kill"])) {
      return "Stop slow requests and return a timeout error";
    }
    return "Return a timeout error";
  }
  if (
    includesAny(text, ["stop them", "stop it", "stop the", "cancel them"]) &&
    includesAny(text, ["too long", "forever", "timeout", "slow"])
  ) {
    return "Stop slow requests";
  }
  if (includesAny(text, ["run forever", "running forever", "letting them run"])) {
    return "Prevent requests from running indefinitely";
  }
  if (includesAny(text, ["taking too long", "too long"])) {
    return "Handle requests that take too long";
  }
  if (includesAny(text, ["concurrent", "usage grows", "unbounded"])) {
    return "Stay safe as usage grows";
  }

  // Fallback: first clause / short slice — not a paraphrase invention of new facts.
  const firstSentence = text.split(/(?<=[.!?])\s+/)[0] || text;
  if (firstSentence.length <= 72) return firstSentence.replace(/[.!?]+$/, "");
  return `${firstSentence.slice(0, 69).trim()}…`;
}

/**
 * Non-authoritative delivery bullets grounded only in phrases present in source.
 * @returns {string[]}
 */
export function deriveDeliveryBullets(sourceText) {
  const text = normalize(sourceText);
  if (!text) return [];
  const bullets = [];
  const lower = text.toLowerCase();

  if (includesAny(lower, ["taking too long", "too long", "slow"])) {
    bullets.push("Detect requests that run too long");
  }
  if (
    includesAny(lower, ["stop them", "stop it", "stop the", "cancel", "abort"]) &&
    includesAny(lower, ["too long", "forever", "timeout", "slow", "them"])
  ) {
    bullets.push("Stop slow requests instead of letting them run forever");
  } else if (includesAny(lower, ["run forever", "running forever", "letting them run forever"])) {
    bullets.push("Prevent requests from running indefinitely");
  }
  if (includesAny(lower, ["timeout error", "send back a timeout", "return a timeout"])) {
    bullets.push("Return a timeout error when a request is stopped");
  }
  if (includesAny(lower, ["automated tests", "tests that prove", "tests that cover"])) {
    bullets.push("Add automated tests for the important paths");
  }
  if (includesAny(lower, ["concurrent", "usage grows", "unbounded"])) {
    bullets.push("Keep behavior safe under growth / concurrency");
  }
  if (includesAny(lower, ["revise the handoff", "both product and engineering"])) {
    bullets.push("Allow product and engineering to revise before acceptance");
  }
  if (includesAny(lower, ["clarifications and engineer notes", "same agreement"])) {
    bullets.push("Keep shared notes visible on the handoff");
  }
  if (includesAny(lower, ["renderrepo handoff", "fully agreed"])) {
    bullets.push("Show drafted vs waiting vs agreed status clearly");
  }

  // Deduplicate while preserving order.
  return [...new Set(bullets)];
}

/**
 * Suggested clarification questions when source is vague.
 * Does not invent answers or requirements.
 * @returns {string[]}
 */
export function deriveDeliveryQuestions(sourceText, existingQuestionTexts = []) {
  const text = normalize(sourceText);
  if (!text) return [];
  const existing = new Set(
    (existingQuestionTexts || []).map((q) => normalize(q).toLowerCase())
  );
  const out = [];

  const maybeAdd = (q) => {
    const key = q.toLowerCase();
    if (existing.has(key)) return;
    if (out.some((x) => x.toLowerCase() === key)) return;
    out.push(q);
  };

  const mentionsTimeoutOrSlow =
    includesAny(text, [
      "timeout",
      "too long",
      "forever",
      "slow",
      "taking too long",
    ]);

  if (mentionsTimeoutOrSlow && !hasConcreteDuration(text)) {
    maybeAdd("How long should Fastify wait before timing out?");
  }
  if (
    mentionsTimeoutOrSlow &&
    includesAny(text, ["them", "some requests", "requests"]) &&
    !hasConcreteRouteScope(text)
  ) {
    maybeAdd("Should this apply to all requests or only selected routes?");
  }
  if (
    includesAny(text, ["timeout error", "error"]) &&
    !hasConcreteErrorBody(text)
  ) {
    maybeAdd("What should the timeout error response include?");
  }

  return out;
}

/**
 * Suggested delivery shape categories — guidance only.
 * @returns {{ backendBehavior: string[], userVisibleBehavior: string[], errorEdgeBehavior: string[], openQuestions: string[] }}
 */
export function deriveSuggestedDeliveryShape(input = {}) {
  const promiseTexts = Array.isArray(input.promiseTexts) ? input.promiseTexts : [];
  const combined = normalize(
    [...promiseTexts, input.requestText || ""].filter(Boolean).join(" ")
  );
  // Caller should pass founder-facing open texts only; drop empty.
  const openQuestions = Array.isArray(input.openQuestionTexts)
    ? input.openQuestionTexts.map(normalize).filter(Boolean)
    : [];

  const backendBehavior = [];
  const userVisibleBehavior = [];
  const errorEdgeBehavior = [];

  if (
    includesAny(combined, ["stop", "cancel", "abort"]) &&
    includesAny(combined, ["too long", "forever", "timeout", "slow"])
  ) {
    backendBehavior.push("Stop requests that take too long");
  } else if (includesAny(combined, ["taking too long", "too long"])) {
    backendBehavior.push("Handle requests that take too long");
  }

  if (includesAny(combined, ["timeout error", "send back a timeout", "return a timeout"])) {
    userVisibleBehavior.push("Return a timeout error");
  }

  if (includesAny(combined, ["run forever", "letting them run forever"])) {
    errorEdgeBehavior.push("Do not let matching requests run indefinitely");
  }

  // Prefer explicit founder-facing opens from the caller; only derive extras when none provided.
  const derivedQs =
    openQuestions.length > 0 ? [] : deriveDeliveryQuestions(combined, openQuestions);
  const allQuestions = [...openQuestions];
  for (const q of derivedQs) {
    if (!allQuestions.some((x) => x.toLowerCase() === q.toLowerCase())) {
      allQuestions.push(q);
    }
  }

  return {
    backendBehavior: [...new Set(backendBehavior)],
    userVisibleBehavior: [...new Set(userVisibleBehavior)],
    errorEdgeBehavior: [...new Set(errorEdgeBehavior)],
    openQuestions: allQuestions,
  };
}

/**
 * Full presentation model for one contractual criterion.
 * Authoritative fields: sourceText, criterionId.
 * Guidance fields: title, deliveryBullets, suggestedQuestions.
 */
export function buildPromisePresentation(criterion, draft, openQuestionTexts = []) {
  const sourceText =
    normalize(
      // Prefer exact source clause; fall back to collapsed promise text.
      (function resolveSource() {
        const ids = Array.isArray(criterion?.sourceClauseIds)
          ? criterion.sourceClauseIds
          : [];
        const clauses = Array.isArray(draft?.sourceClauses) ? draft.sourceClauses : [];
        for (const id of ids) {
          const match = clauses.find((c) => c.clauseId === id);
          if (match?.text) return match.text;
        }
        return "";
      })()
    ) || normalize(criterion?.action) || normalize(criterion?.actor) || "";

  const title = derivePromiseDisplayTitle(sourceText);
  const deliveryBullets = deriveDeliveryBullets(sourceText);
  const suggestedQuestions = deriveDeliveryQuestions(sourceText, openQuestionTexts);
  const isFounderWorded =
    /\b(i want|i'd like|i would like|we want|we need)\b/i.test(sourceText) ||
    (sourceText.length > 90 && deliveryBullets.length > 0);

  return {
    criterionId: criterion?.criterionId || null,
    title,
    sourceText,
    deliveryBullets,
    suggestedQuestions,
    isFounderWorded,
    authoritative: false, // title/bullets are guidance
  };
}

/**
 * Aggregate presentations for a draft.
 */
export function buildHandoffPromisePresentations(draft, criteria, openQuestions = []) {
  const openTexts = (openQuestions || []).map((q) => q.text || q).filter(Boolean);
  const presentations = (criteria || []).map((c) =>
    buildPromisePresentation(c, draft, openTexts)
  );
  const promiseTexts = presentations.map((p) => p.sourceText).filter(Boolean);
  const requestText =
    presentations.length === 1
      ? presentations[0].sourceText
      : promiseTexts.join(" ");
  // Open product questions are attached in the handoff agreement UI from the
  // founder-facing partition — do not feed raw server/internal questions here.
  const deliveryShape = deriveSuggestedDeliveryShape({
    promiseTexts,
    requestText,
    openQuestionTexts: [],
  });
  // Merge suggested questions from presentations that aren't already open.
  const guidanceQuestions = [];
  for (const p of presentations) {
    for (const q of p.suggestedQuestions || []) {
      if (
        !openTexts.some((t) => t.toLowerCase() === q.toLowerCase()) &&
        !guidanceQuestions.some((t) => t.toLowerCase() === q.toLowerCase())
      ) {
        guidanceQuestions.push(q);
      }
    }
  }
  return {
    presentations,
    deliveryShape,
    guidanceQuestions,
    showCorePromiseLayout:
      presentations.length === 1 && presentations[0]?.isFounderWorded,
  };
}
